import { createPlanAssetPlacement as createImportedSvgPlacement, planAssetLocalPointToWorld as importedSvgLocalPointToWorld, planAssetWorldPointToLocal as importedSvgWorldPointToLocal } from '../../plugin-sdk/plan_asset_placement.mjs';
// Pure geometry and indexing helpers for imported 2D SVG assets.
//
// This module deliberately has no DOM or application-state dependency. The
// interactive runtime in imported_svg_asset.js feeds it the existing plan
// camera/placement values; tests and the deterministic benchmark can exercise
// exactly the same culling math without booting the application.

const DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG = Object.freeze({
  svgViewportVirtualizationEnabled: true,
  minDrawingElements: 1500,
  targetUnitsPerCell: 192,
  overscanRatio: 0.25,
  hysteresisRatio: 0.10,
  maxCellsPerUnit: 64
});

let featureFlagOverride = null;

function finiteNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function positiveNumber(value, fallback = 1) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
}

function runtimeSvgViewportVirtualizationFlag() {
  const configured = globalThis?.KS_STUDIO_CONFIG?.svgViewportVirtualizationEnabled;
  if (typeof configured === 'boolean') return configured;
  const direct = globalThis?.svgViewportVirtualizationEnabled;
  if (typeof direct === 'boolean') return direct;
  return DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.svgViewportVirtualizationEnabled;
}

function svgViewportVirtualizationEnabled() {
  return typeof featureFlagOverride === 'boolean'
    ? featureFlagOverride
    : runtimeSvgViewportVirtualizationFlag();
}

function setSvgViewportVirtualizationEnabled(value) {
  featureFlagOverride = typeof value === 'boolean' ? value : null;
  return svgViewportVirtualizationEnabled();
}

function resetSvgViewportVirtualizationEnabled() {
  featureFlagOverride = null;
  return svgViewportVirtualizationEnabled();
}

function normalizeAabb(value, fallback = null) {
  if (!value) return fallback;
  const minX = finiteNumber(value.minX, NaN);
  const minZ = finiteNumber(value.minZ ?? value.minY, NaN);
  const maxX = finiteNumber(value.maxX, NaN);
  const maxZ = finiteNumber(value.maxZ ?? value.maxY, NaN);
  if (![minX, minZ, maxX, maxZ].every(Number.isFinite)) return fallback;
  return {
    minX: Math.min(minX, maxX),
    minZ: Math.min(minZ, maxZ),
    maxX: Math.max(minX, maxX),
    maxZ: Math.max(minZ, maxZ)
  };
}

function aabbWidth(bounds) {
  return Math.max(0, finiteNumber(bounds?.maxX) - finiteNumber(bounds?.minX));
}

function aabbDepth(bounds) {
  return Math.max(0, finiteNumber(bounds?.maxZ) - finiteNumber(bounds?.minZ));
}

function aabbIntersects(left, right) {
  const a = normalizeAabb(left);
  const b = normalizeAabb(right);
  if (!a || !b) return false;
  return a.minX <= b.maxX
    && a.maxX >= b.minX
    && a.minZ <= b.maxZ
    && a.maxZ >= b.minZ;
}

function mergeAabbs(left, right) {
  const a = normalizeAabb(left);
  const b = normalizeAabb(right);
  if (!a) return b;
  if (!b) return a;
  return {
    minX: Math.min(a.minX, b.minX),
    minZ: Math.min(a.minZ, b.minZ),
    maxX: Math.max(a.maxX, b.maxX),
    maxZ: Math.max(a.maxZ, b.maxZ)
  };
}

function expandAabb(bounds, ratio = 0, minimum = 0) {
  const rect = normalizeAabb(bounds);
  if (!rect) return null;
  const safeRatio = Math.max(0, finiteNumber(ratio));
  const safeMinimum = Math.max(0, finiteNumber(minimum));
  const padX = Math.max(safeMinimum, aabbWidth(rect) * safeRatio);
  const padZ = Math.max(safeMinimum, aabbDepth(rect) * safeRatio);
  return {
    minX: rect.minX - padX,
    minZ: rect.minZ - padZ,
    maxX: rect.maxX + padX,
    maxZ: rect.maxZ + padZ
  };
}

function aabbFromPoints(points = []) {
  let bounds = null;
  for (const point of points) {
    const x = finiteNumber(point?.x, NaN);
    const z = finiteNumber(point?.z ?? point?.y, NaN);
    if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
    bounds = mergeAabbs(bounds, { minX: x, minZ: z, maxX: x, maxZ: z });
  }
  return bounds;
}

function aabbCorners(bounds) {
  const rect = normalizeAabb(bounds);
  if (!rect) return [];
  return [
    { x: rect.minX, z: rect.minZ },
    { x: rect.maxX, z: rect.minZ },
    { x: rect.maxX, z: rect.maxZ },
    { x: rect.minX, z: rect.maxZ }
  ];
}

function importedSvgPlacementWorldAabb(placement, localBounds = null) {
  const source = placement?.sourceBounds;
  if (!source) return null;
  const bounds = normalizeAabb(localBounds) || {
    minX: source.minX,
    minZ: source.minZ,
    maxX: source.maxX,
    maxZ: source.maxZ
  };
  return aabbFromPoints(aabbCorners(bounds).map(point => importedSvgLocalPointToWorld(point, placement)));
}

function importedSvgWorldAabbToLocalAabb(worldBounds, placement) {
  return aabbFromPoints(aabbCorners(worldBounds).map(point => (
    importedSvgWorldPointToLocal(point, placement)
  )));
}

function worldViewportAabb(screenToWorld, width, height) {
  if (typeof screenToWorld !== 'function') return null;
  const w = Math.max(1, finiteNumber(width, 1));
  const h = Math.max(1, finiteNumber(height, 1));
  return aabbFromPoints([
    screenToWorld(0, 0),
    screenToWorld(w, 0),
    screenToWorld(w, h),
    screenToWorld(0, h)
  ]);
}

const SVG_SPATIAL_DENSITY_SAMPLE_LIMIT = 4096;
const SVG_SPATIAL_OUTLIER_FENCE_MULTIPLIER = 3;

function importedSvgBoundsCenter(bounds) {
  const rect = normalizeAabb(bounds);
  if (!rect) return null;
  // Dividing first avoids overflowing when two large, same-sign coordinates
  // are added before the division.
  return {
    x: rect.minX / 2 + rect.maxX / 2,
    z: rect.minZ / 2 + rect.maxZ / 2
  };
}

function importedSvgFiniteExtent(minimum, maximum) {
  const extent = maximum - minimum;
  return Number.isFinite(extent) && extent >= 0 ? extent : 0;
}

function importedSvgDensitySample(entries) {
  if (entries.length <= SVG_SPATIAL_DENSITY_SAMPLE_LIMIT) return entries;
  const sample = [];
  const step = entries.length / SVG_SPATIAL_DENSITY_SAMPLE_LIMIT;
  for (let index = 0; index < SVG_SPATIAL_DENSITY_SAMPLE_LIMIT; index += 1) {
    sample.push(entries[Math.floor(index * step)]);
  }
  return sample;
}

function importedSvgSortedPercentile(sortedValues, ratio) {
  if (!sortedValues.length) return 0;
  const index = Math.min(
    sortedValues.length - 1,
    Math.max(0, Math.floor((sortedValues.length - 1) * ratio))
  );
  return sortedValues[index];
}

function importedSvgOutlierFence(values) {
  if (values.length < 8) return { min: -Infinity, max: Infinity };
  const sorted = [...values].sort((left, right) => left - right);
  const lowerQuartile = importedSvgSortedPercentile(sorted, 0.25);
  const upperQuartile = importedSvgSortedPercentile(sorted, 0.75);
  const interquartileRange = upperQuartile - lowerQuartile;
  if (!Number.isFinite(interquartileRange) || interquartileRange < 0) {
    return { min: -Infinity, max: Infinity };
  }
  const margin = interquartileRange * SVG_SPATIAL_OUTLIER_FENCE_MULTIPLIER;
  return {
    min: Number.isFinite(lowerQuartile - margin) ? lowerQuartile - margin : -Infinity,
    max: Number.isFinite(upperQuartile + margin) ? upperQuartile + margin : Infinity
  };
}

function importedSvgRobustDensity(entries) {
  if (!entries.length) return { bounds: null, count: 0 };
  const sample = importedSvgDensitySample(entries).map(entry => ({
    ...entry,
    center: importedSvgBoundsCenter(entry.bounds),
    width: importedSvgFiniteExtent(entry.bounds.minX, entry.bounds.maxX),
    depth: importedSvgFiniteExtent(entry.bounds.minZ, entry.bounds.maxZ)
  }));
  const xFence = importedSvgOutlierFence(sample.map(entry => entry.center.x));
  const zFence = importedSvgOutlierFence(sample.map(entry => entry.center.z));
  let inliers = sample.filter(entry => (
    entry.center.x >= xFence.min
      && entry.center.x <= xFence.max
      && entry.center.z >= zFence.min
      && entry.center.z <= zFence.max
  ));
  // Two independent axis fences can be too strict for unusual sparse shapes.
  // Falling back below 25% keeps the sizing representative without allowing a
  // handful of remote CAD artefacts to stretch it across the entire plan.
  if (inliers.length < Math.max(1, Math.floor(sample.length * 0.25))) inliers = sample;

  const widths = inliers
    .map(entry => entry.width)
    .filter(value => Number.isFinite(value))
    .sort((left, right) => left - right);
  const depths = inliers
    .map(entry => entry.depth)
    .filter(value => Number.isFinite(value))
    .sort((left, right) => left - right);
  const padX = importedSvgSortedPercentile(widths, 0.5) / 2;
  const padZ = importedSvgSortedPercentile(depths, 0.5) / 2;
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const entry of inliers) {
    minX = Math.min(minX, entry.center.x);
    minZ = Math.min(minZ, entry.center.z);
    maxX = Math.max(maxX, entry.center.x);
    maxZ = Math.max(maxZ, entry.center.z);
  }
  if (![minX, minZ, maxX, maxZ].every(Number.isFinite)) return { bounds: null, count: inliers.length };
  const estimatedInlierCount = Math.max(1, Math.round(entries.length * inliers.length / sample.length));
  return {
    bounds: normalizeAabb({
      minX: Number.isFinite(minX - padX) ? minX - padX : minX,
      minZ: Number.isFinite(minZ - padZ) ? minZ - padZ : minZ,
      maxX: Number.isFinite(maxX + padX) ? maxX + padX : maxX,
      maxZ: Number.isFinite(maxZ + padZ) ? maxZ + padZ : maxZ
    }),
    count: estimatedInlierCount
  };
}

function importedSvgCellRangeSpan(minimum, maximum) {
  if (!Number.isSafeInteger(minimum) || !Number.isSafeInteger(maximum) || maximum < minimum) return Infinity;
  const difference = maximum - minimum;
  if (!Number.isSafeInteger(difference)) return Infinity;
  return difference + 1;
}

function importedSvgCellRangeCount(range, limit = Number.MAX_SAFE_INTEGER) {
  if (!range) return Infinity;
  const width = importedSvgCellRangeSpan(range.minX, range.maxX);
  const depth = importedSvgCellRangeSpan(range.minZ, range.maxZ);
  if (!Number.isFinite(width) || !Number.isFinite(depth)) return Infinity;
  const safeLimit = Number.isFinite(limit) && limit >= 1 ? limit : Number.MAX_SAFE_INTEGER;
  if (width > safeLimit || depth > safeLimit || width > safeLimit / depth) return Infinity;
  return width * depth;
}

class StaticSvgSpatialIndex {
  constructor(units = [], options = {}) {
    this.unitsById = new Map();
    this.cells = new Map();
    this.occupiedCells = [];
    this.globalIds = new Set();
    this.alwaysIds = new Set();
    this.bounds = null;
    this.indexRange = null;
    this.maxCellsPerUnit = Math.max(1, Math.floor(positiveNumber(
      options.maxCellsPerUnit,
      DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.maxCellsPerUnit
    )));

    for (const unit of units) {
      if (!unit?.id) continue;
      this.unitsById.set(unit.id, unit);
    }

    const boundedEntries = [];
    for (const unit of this.unitsById.values()) {
      const bounds = normalizeAabb(unit.bounds);
      if (unit.cullable === false || !bounds) {
        this.alwaysIds.add(unit.id);
      } else {
        this.bounds = this.bounds
          ? {
              minX: Math.min(this.bounds.minX, bounds.minX),
              minZ: Math.min(this.bounds.minZ, bounds.minZ),
              maxX: Math.max(this.bounds.maxX, bounds.maxX),
              maxZ: Math.max(this.bounds.maxZ, bounds.maxZ)
            }
          : { ...bounds };
        boundedEntries.push({
          unit,
          bounds
        });
      }
    }

    const density = importedSvgRobustDensity(boundedEntries);
    const indexedCount = Math.max(1, density.count || boundedEntries.length);
    const targetUnitsPerCell = positiveNumber(
      options.targetUnitsPerCell,
      DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.targetUnitsPerCell
    );
    const densityBounds = density.bounds || this.bounds;
    const densityArea = aabbWidth(densityBounds) * aabbDepth(densityBounds);
    const area = Number.isFinite(densityArea) ? Math.max(1, densityArea) : 1;
    this.cellSize = positiveNumber(options.cellSize, Math.sqrt(area / Math.max(1, indexedCount / targetUnitsPerCell)));
    if (!Number.isFinite(this.cellSize) || this.cellSize <= 0) this.cellSize = 1;

    // Classify oversized/unindexable geometry before allocating any local
    // cells. It stays queryable through the global list but cannot distort the
    // occupied local range or make a huge query walk billions of empty cells.
    const localEntries = [];
    for (const entry of boundedEntries) {
      const range = this.#normalizedCellRange(entry.bounds);
      if (importedSvgCellRangeCount(range, this.maxCellsPerUnit) > this.maxCellsPerUnit) {
        this.globalIds.add(entry.unit.id);
      } else {
        entry.range = range;
        localEntries.push(entry);
      }
    }
    for (const entry of localEntries) this.#insertLocal(entry.unit, entry.range);
  }

  #cellRange(bounds) {
    const rect = normalizeAabb(bounds);
    if (!rect) return null;
    return this.#normalizedCellRange(rect);
  }

  #normalizedCellRange(rect) {
    const range = {
      minX: Math.floor(rect.minX / this.cellSize),
      minZ: Math.floor(rect.minZ / this.cellSize),
      maxX: Math.floor(rect.maxX / this.cellSize),
      maxZ: Math.floor(rect.maxZ / this.cellSize)
    };
    return Object.values(range).every(Number.isFinite) ? range : null;
  }

  #insertLocal(unit, range) {
    for (let x = range.minX; x <= range.maxX; x += 1) {
      for (let z = range.minZ; z <= range.maxZ; z += 1) {
        const key = `${x}:${z}`;
        let ids = this.cells.get(key);
        if (!ids) {
          ids = [];
          this.cells.set(key, ids);
          this.occupiedCells.push({ x, z, ids });
          this.indexRange = this.indexRange
            ? {
                minX: Math.min(this.indexRange.minX, x),
                minZ: Math.min(this.indexRange.minZ, z),
                maxX: Math.max(this.indexRange.maxX, x),
                maxZ: Math.max(this.indexRange.maxZ, z)
              }
            : { minX: x, minZ: z, maxX: x, maxZ: z };
        }
        ids.push(unit.id);
      }
    }
  }

  query(bounds) {
    const rect = normalizeAabb(bounds);
    const ids = new Set(this.alwaysIds);
    if (!rect) return [...ids];
    for (const id of this.globalIds) ids.add(id);
    const rawRange = this.#cellRange(rect);
    const range = rawRange && this.indexRange
      ? {
          minX: Math.max(rawRange.minX, this.indexRange.minX),
          minZ: Math.max(rawRange.minZ, this.indexRange.minZ),
          maxX: Math.min(rawRange.maxX, this.indexRange.maxX),
          maxZ: Math.min(rawRange.maxZ, this.indexRange.maxZ)
        }
      : null;
    if (range && range.minX <= range.maxX && range.minZ <= range.maxZ) {
      const directIterationLimit = Math.max(64, this.cells.size * 4);
      const queriedCellCount = importedSvgCellRangeCount(range, directIterationLimit);
      if (queriedCellCount <= directIterationLimit) {
        for (let x = range.minX; x <= range.maxX; x += 1) {
          for (let z = range.minZ; z <= range.maxZ; z += 1) {
            for (const id of this.cells.get(`${x}:${z}`) || []) ids.add(id);
          }
        }
      } else {
        // Sparse or extremely zoomed-out queries iterate only cells which
        // really exist. Runtime is therefore bounded by the index size rather
        // than the numerical distance between remote SVG artefacts.
        for (const cell of this.occupiedCells) {
          if (cell.x < range.minX || cell.x > range.maxX || cell.z < range.minZ || cell.z > range.maxZ) continue;
          for (const id of cell.ids) ids.add(id);
        }
      }
    }
    return [...ids].filter(id => {
      const unit = this.unitsById.get(id);
      return unit?.cullable === false || aabbIntersects(unit?.bounds, rect);
    });
  }

  ordered(ids) {
    return [...new Set(ids || [])]
      .filter(id => this.unitsById.has(id))
      .sort((left, right) => finiteNumber(this.unitsById.get(left)?.order) - finiteNumber(this.unitsById.get(right)?.order));
  }

  stats() {
    return {
      unitCount: this.unitsById.size,
      alwaysCount: this.alwaysIds.size,
      globalCount: this.globalIds.size,
      localCount: this.unitsById.size - this.alwaysIds.size - this.globalIds.size,
      cellCount: this.cells.size,
      cellSize: this.cellSize,
      indexRange: this.indexRange ? { ...this.indexRange } : null
    };
  }
}

function selectSvgVisibleUnitIds(index, enterBounds, exitBounds, previousIds = [], pinnedIds = []) {
  if (!index) return [];
  const entering = new Set(index.query(enterBounds));
  const retainedCandidates = new Set(index.query(exitBounds || enterBounds));
  for (const id of previousIds || []) {
    if (retainedCandidates.has(id)) entering.add(id);
  }
  for (const id of pinnedIds || []) entering.add(id);
  return index.ordered(entering);
}

function sameOrderedIds(left = [], right = []) {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

export {
  DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG,
  StaticSvgSpatialIndex,
  aabbCorners,
  aabbDepth,
  aabbFromPoints,
  aabbIntersects,
  aabbWidth,
  createImportedSvgPlacement,
  expandAabb,
  importedSvgLocalPointToWorld,
  importedSvgPlacementWorldAabb,
  importedSvgWorldAabbToLocalAabb,
  importedSvgWorldPointToLocal,
  mergeAabbs,
  normalizeAabb,
  resetSvgViewportVirtualizationEnabled,
  sameOrderedIds,
  selectSvgVisibleUnitIds,
  setSvgViewportVirtualizationEnabled,
  svgViewportVirtualizationEnabled,
  worldViewportAabb
};
